/**
 * Resolves the federation manifest the shell hands to `initFederation`, and the two query-param
 * overrides layered on top of it when `environment.allowManifestOverrides` is `true` — see `src/main.ts`.
 *
 * Every remotes-hosted reference (the manifest itself, and every entry inside it) is a path relative
 * to `environment.remotesBaseUrl`, so a reference can only ever name bytes already published to that
 * origin. Two exceptions, both loopback-only, exist to point a deployed shell at a locally-served
 * remote: a bare port number (`?mfe-remote:<id>=4202`) and a full `http://localhost:<port>/...` URL.
 * Anything else — a different origin, `//host/...`, `file:`, or a relative value with no base
 * configured — is dropped with a console warning, never applied.
 *
 *  - `?mfe-remote:<id>=<value>` overrides individual entries within the manifest the shell fetches.
 *  - `?mfe-manifest=<value>` replaces the manifest *source* wholesale — the mechanism behind
 *    previewing (docs/mfe/mfe-artefact-lifecycle.md). Its value is one of three things:
 *      - an explicit path, e.g. a coordinate's own
 *        `remotes/<id>/<version>/<sha>/federation.manifest.json`;
 *      - `<remote-id>@<tag>` — one remote varied inside the live composition, expanded to
 *        `remotes/<remote-id>/previews/<tag>/federation.manifest.json`. Only `branch-*` and `pin-*`
 *        tags publish one;
 *      - `@branch-<slug>` — a whole branch's composition, expanded to
 *        `manifests/previews/branch-<slug>/federation.manifest.json`. This is what
 *        `compose-composition.mjs --branch` releases for a feature branch, so it shows every remote
 *        that branch published at once, rather than one at a time.
 *    Any other tag kind is rejected with a message naming the accepted forms rather than silently
 *    404ing.
 *
 * Both overrides are query-string only, never persisted.
 *
 * **The gate is `allowManifestOverrides`, never `environment.name` or the `production` boolean.**
 * `production` is set `true` for `staging` and `pwa-local` as well as `production` (see
 * `@azzo/energyx-contracts`'s `plugin-compatibility-policy.ts` comment on why runtime *tier* derivation
 * already avoids it), so it can't identify "the production environment" either. Keying on
 * `name === 'production'` would work today but is fail-*open*: any environment whose file is named
 * anything else — a hypothetical `environment.production.eu.ts` with `name: 'production-eu'` — would
 * silently permit overrides in a real deployment. `allowManifestOverrides` is optional and **absent
 * means false**, so a new environment that forgets to set it is safe by default.
 */

/** `?mfe-remote:<remoteId>=<value>` — one query param per overridden remote. */
const OVERRIDE_QUERY_PREFIX = 'mfe-remote:';

/** `?mfe-manifest=<value>` — replaces the manifest source wholesale. */
const WHOLESALE_OVERRIDE_QUERY_PARAM = 'mfe-manifest';

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

export interface ManifestOverrideSource {
  readonly search: string;
}

/**
 * The subset of `Environment` this module needs, so it has no dependency on contracts' full shape.
 * Mirrors contracts' `FederationManifestSource` structurally rather than importing it, for the same
 * reason.
 */
export interface FederationEnvironment {
  readonly name: string;
  readonly remotesBaseUrl?: string;
  readonly federationManifest: { readonly kind: 'hosted'; readonly path: string } | { readonly kind: 'local' };
  /** Fail-closed: absent or `false` means neither override param is applied. See the module doc comment. */
  readonly allowManifestOverrides?: boolean;
}

function isAbsoluteValue(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('//');
}

/** A fetched manifest entry's expected shape once resolved: `.../remotes/<id>/<version>/<sha>/remoteEntry.json`. */
const COORDINATE_PATH_PATTERN = /^remotes\/[^/]+\/[^/]+\/[^/]+\/remoteEntry\.json$/;

/** `energyx-remote-evlm`-shaped slug — same grammar as `scripts/deployable-artefact/coordinates.mjs`'s `isValidRemoteIdSlug`. */
const REMOTE_ID_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Only these two tag kinds ever publish a per-remote composition preview — see `write-preview-manifest.mjs`. */
const PREVIEWABLE_TAG_PATTERN = /^(?:branch|pin)-[a-z0-9._-]+$/;

/**
 * A branch composition is published for a `branch-*` tag only: it is the whole environment as one
 * branch sees it, so there is no per-remote `pin-*` equivalent — see `compose-composition.mjs --branch`.
 */
const BRANCH_TAG_PATTERN = /^branch-[a-z0-9._-]+$/;

function isLoopbackUrl(url: URL): boolean {
  return (url.protocol === 'http:' || url.protocol === 'https:') && LOOPBACK_HOSTNAMES.has(url.hostname);
}

/**
 * Resolves one remotes-hosted reference. A relative value resolves against `base` and must stay on
 * `base`'s origin (no `../` escape); a bare port number (only when `allowPortShorthand`) expands to
 * `http://localhost:<port>/remoteEntry.json`; a loopback absolute URL, or an absolute URL already on
 * `base`'s own origin, passes through unchanged — the latter is exactly as trustworthy as a relative
 * path resolving to that origin, and keeps this tolerant of already-fully-qualified entries (e.g. a
 * manifest published before entries moved to relative paths, until it's next released). Any other
 * absolute value — a different origin, `file:`, a protocol-relative `//host/...` — and any relative
 * value with no `base` configured, is rejected. Returns `undefined` on rejection, never throws.
 */
export function resolveRemoteUrl(
  value: string,
  base: string | undefined,
  options: { allowPortShorthand?: boolean } = {}
): string | undefined {
  const trimmed = value.trim();

  if (options.allowPortShorthand && /^\d+$/.test(trimmed)) {
    return `http://localhost:${trimmed}/remoteEntry.json`;
  }

  if (isAbsoluteValue(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (isLoopbackUrl(url)) return url.href;
      if (base && url.origin === new URL(base).origin) return url.href;
      return undefined;
    } catch {
      return undefined;
    }
  }

  if (!base) return undefined;

  // Rejected outright rather than relied on URL normalisation to clamp at the origin root: a `..`
  // segment has no legitimate reason to appear in a coordinate path, so treat it as intent to escape.
  if (trimmed.split('/').some((segment) => segment === '..')) return undefined;

  try {
    const baseUrl = new URL(base);
    const resolved = new URL(trimmed, baseUrl);
    const basePath = baseUrl.pathname.endsWith('/') ? baseUrl.pathname : `${baseUrl.pathname}/`;
    if (resolved.origin !== baseUrl.origin || !resolved.pathname.startsWith(basePath)) {
      return undefined;
    }
    return resolved.href;
  } catch {
    return undefined;
  }
}

/** Reads and resolves `?mfe-remote:<remoteId>=<value>` entries from a query string against `base`. */
export function parseManifestOverrides(source: ManifestOverrideSource, base: string | undefined): ReadonlyMap<string, string> {
  const params = new URLSearchParams(source.search);
  const overrides = new Map<string, string>();

  for (const [key, value] of params.entries()) {
    if (!key.startsWith(OVERRIDE_QUERY_PREFIX)) continue;

    const remoteId = key.slice(OVERRIDE_QUERY_PREFIX.length);
    const resolved = resolveRemoteUrl(value, base, { allowPortShorthand: true });
    if (!resolved) {
      console.warn(`[federation-manifest-resolver] Rejected override for "${remoteId}": "${value}" did not resolve to an allowed URL.`);
      continue;
    }

    overrides.set(remoteId, resolved);
  }

  return overrides;
}

/** Overlays resolved overrides onto the fetched manifest record. Never mutates the input. */
export function applyManifestOverrides(
  manifest: Readonly<Record<string, string>>,
  overrides: ReadonlyMap<string, string>
): Record<string, string> {
  return { ...manifest, ...Object.fromEntries(overrides) };
}

/**
 * Expands a tag reference to the blob path holding that composition — pure synchronous string work,
 * resolved by the caller through the ordinary `resolveRemoteUrl` origin/escape checks afterwards. Two
 * grammars, distinguished by whether a remote is named before the `@`:
 *   - `<remote-id>@<tag>` → `remotes/<remote-id>/previews/<tag>/federation.manifest.json` — the live
 *     composition with that one remote swapped for the build the tag names.
 *   - `@branch-<slug>` → `manifests/previews/branch-<slug>/federation.manifest.json` — the whole
 *     composition as that branch resolves it, across every remote the branch published. The empty
 *     remote-id position reads as "no particular remote", which is exactly what it is.
 *
 * Three outcomes:
 *   - `undefined` — `value` isn't this grammar at all (no bare `@`, or looks like an absolute/protocol-
 *     relative URL where `@` could appear in userinfo) — caller falls through to path/URL handling.
 *   - `null` — it IS `<something>@<something>`, but invalid: a malformed remote-id slug, or (the
 *     interesting case) a syntactically fine tag of a kind that publishes nothing at that path —
 *     `build-*`, `env-*` and a bare semver never publish a preview, and a branch composition exists for
 *     `branch-*` only. Resolving any of them would silently 404, so the caller reports instead.
 *   - a string — the expanded path, still relative, still subject to every existing check.
 */
function expandTagReference(value: string): string | null | undefined {
  if (isAbsoluteValue(value) || !value.includes('@')) return undefined;

  const at = value.indexOf('@');
  const remoteId = value.slice(0, at);
  const tag = value.slice(at + 1);

  // Checked before the remote-id test below, which an empty id would otherwise reject outright.
  if (remoteId === '') {
    return BRANCH_TAG_PATTERN.test(tag) ? `manifests/previews/${tag}/federation.manifest.json` : null;
  }

  if (!REMOTE_ID_SLUG_PATTERN.test(remoteId)) return null;
  if (!PREVIEWABLE_TAG_PATTERN.test(tag)) return null;

  return `remotes/${remoteId}/previews/${tag}/federation.manifest.json`;
}

/**
 * Reads and resolves the `?mfe-manifest=<value>` param against `base`. `value` is either an explicit
 * coordinate/preview path (or absolute/loopback URL, handled by `resolveRemoteUrl` as always) or the
 * `<remote-id>@<tag>` shorthand, expanded first. Returns `undefined` if absent or unresolvable — every
 * rejection path logs *why*, since each is a knowable failure, not a generic 404.
 */
export function parseWholesaleManifestOverride(source: ManifestOverrideSource, base: string | undefined): string | undefined {
  const params = new URLSearchParams(source.search);
  const value = params.get(WHOLESALE_OVERRIDE_QUERY_PARAM);
  if (value === null) return undefined;

  const trimmed = value.trim();

  const expanded = expandTagReference(trimmed);
  if (expanded === null) {
    console.warn(
      `[federation-manifest-resolver] Rejected "${WHOLESALE_OVERRIDE_QUERY_PARAM}": "${value}" is not a previewable ` +
      `reference. Use "<remote-id>@<tag>" for one remote (branch-* and pin-* tags only), "@branch-<slug>" for a ` +
      `whole branch's composition, or the coordinate form "remotes/<id>/<version>/<sha>/federation.manifest.json".`
    );
    return undefined;
  }

  if (expanded === undefined && !trimmed.includes('@') && !trimmed.includes('/') && !isAbsoluteValue(trimmed)) {
    console.warn(
      `[federation-manifest-resolver] Rejected "${WHOLESALE_OVERRIDE_QUERY_PARAM}": "${value}" is neither a ` +
      `"<remote-id>@<tag>" reference nor a coordinate path.`
    );
    return undefined;
  }

  const resolved = resolveRemoteUrl(expanded ?? trimmed, base);
  if (!resolved) {
    console.warn(`[federation-manifest-resolver] Rejected "${WHOLESALE_OVERRIDE_QUERY_PARAM}": "${value}" did not resolve to an allowed URL.`);
    return undefined;
  }

  return resolved;
}

/** `true` if `resolvedUrl` is a `remoteEntry.json` coordinate path or a loopback URL — the only two
 * shapes a *fetched* manifest entry may take. Distinct from `resolveRemoteUrl`'s origin/escape checks:
 * this catches a malformed-but-otherwise-resolvable entry (a bare tag token like `"branch-develop"`
 * would resolve cleanly to `<base>/branch-develop` and fail later at import-map time with no useful
 * message) before it's ever handed to `initFederation`. */
function isAcceptableManifestEntryUrl(resolvedUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(resolvedUrl);
  } catch {
    return false;
  }
  if (isLoopbackUrl(parsed)) return true;
  return COORDINATE_PATH_PATTERN.test(parsed.pathname.replace(/^\//, ''));
}

/**
 * Resolves every entry of a fetched manifest against `base`. Every entry is resolved the same way a
 * query-param override is, then additionally checked for shape (see `isAcceptableManifestEntryUrl`);
 * an entry that fails either is dropped with a console warning, never thrown. Unlike the shell's own
 * committed manifest, a wholesale-overridden manifest's content is foreign and attacker-influenceable,
 * so this is applied to every fetched manifest, not only overridden ones.
 */
export function resolveManifestEntries(manifest: Readonly<Record<string, unknown>>, base: string | undefined): Record<string, string> {
  const resolved: Record<string, string> = {};

  for (const [remoteId, value] of Object.entries(manifest)) {
    if (typeof value !== 'string') {
      console.warn(`[federation-manifest-resolver] Entry "${remoteId}" is not a string — dropped.`);
      continue;
    }

    const resolvedUrl = resolveRemoteUrl(value, base);
    if (!resolvedUrl) {
      console.warn(`[federation-manifest-resolver] Entry "${remoteId}": "${value}" did not resolve to an allowed URL — dropped.`);
      continue;
    }

    if (!isAcceptableManifestEntryUrl(resolvedUrl)) {
      console.warn(
        `[federation-manifest-resolver] Entry "${remoteId}": "${value}" did not resolve to a remoteEntry.json ` +
        `coordinate path or a loopback URL — dropped.`
      );
      continue;
    }

    resolved[remoteId] = resolvedUrl;
  }

  return resolved;
}

/**
 * Local-manifest fallback, derived from the shell's own `<base href>`. Used for every environment
 * whose `federationManifest.kind` is `'local'` — every local/debug/test build, where the manifest is
 * still served next to the shell rather than hosted separately (docs/mfe/mfe-artefact-lifecycle.md).
 */
function localFederationManifestUrl(): string {
  const href = typeof document !== 'undefined' ? document.querySelector('base')?.href : undefined;
  const base = href ?? '/';
  return `${base.replace(/\/?$/, '/')}federation.manifest.json`;
}

/**
 * The manifest URL for this environment, per its declared `federationManifest.kind`: `'hosted'`
 * resolves `path` against `remotesBaseUrl`; `'local'` uses the `<base href>`-derived fallback. Throws
 * if `'hosted'` is declared without a `remotesBaseUrl` — a hosted manifest with nothing to resolve
 * against is a misconfiguration, not something to silently fall back from.
 */
export function federationManifestUrl(environment: FederationEnvironment): string {
  if (environment.federationManifest.kind === 'local') {
    return localFederationManifestUrl();
  }

  if (!environment.remotesBaseUrl) {
    throw new Error(
      `environment "${environment.name}" declares federationManifest.kind: 'hosted' but has no remotesBaseUrl to resolve "${environment.federationManifest.path}" against.`
    );
  }

  return new URL(environment.federationManifest.path, environment.remotesBaseUrl).href;
}

/**
 * Fetches the runtime manifest, resolves every entry against `environment.remotesBaseUrl`, and — only
 * when `environment.allowManifestOverrides` is `true` — composes the two override layers on top before
 * `initFederation` resolves the import map:
 *   1. `?mfe-manifest=<value>` replaces the manifest *source* wholesale.
 *   2. `?mfe-remote:<id>=<value>` then overlays individual entries on top.
 * On any failure (fetch, parse, or an entirely-empty result), logs and returns `{}` — the shell still
 * boots, and `PluginManager` surfaces the ordinary "remote never composed" error for any alias that
 * needed it, rather than handing `initFederation` something it can't load.
 */
export async function resolveFederationManifest(
  environment: FederationEnvironment,
  search: string
): Promise<Record<string, string>> {
  const overridesAllowed = environment.allowManifestOverrides === true;

  try {
    const manifestUrl = federationManifestUrl(environment);
    const wholesaleOverrideUrl = overridesAllowed
      ? parseWholesaleManifestOverride({ search }, environment.remotesBaseUrl)
      : undefined;
    const sourceUrl = wholesaleOverrideUrl ?? manifestUrl;

    const rawManifest = (await fetch(sourceUrl).then((res) => res.json())) as Record<string, unknown>;
    const manifest = resolveManifestEntries(rawManifest, environment.remotesBaseUrl);

    if (!overridesAllowed) return manifest;

    const overrides = parseManifestOverrides({ search }, environment.remotesBaseUrl);
    return overrides.size > 0 ? applyManifestOverrides(manifest, overrides) : manifest;
  } catch (err) {
    console.error('Failed to fetch or resolve the federation manifest.', err);
    return {};
  }
}
